import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { sendGoogleEvent } from "./googleTag";

/**
 * Stream Core Web Vitals to GA4 using the convention recommended by Google:
 * https://developers.google.com/analytics/devguides/collection/ga4/web-vitals
 *
 * These metrics drive Google's page-experience ranking signal. CLS / INP / LCP
 * are the three "Core" Web Vitals; FCP and TTFB are diagnostic. We send all
 * five so the admin can see the full picture in GA4's "Custom Events" report.
 *
 * Value semantics:
 *   • CLS reported × 1000 (otherwise GA4 rounds 0.05 → 0).
 *   • Others reported as milliseconds (rounded to integer).
 *   • `event_label` carries the metric id (unique per page load) so unique
 *     metric reports can be deduplicated in GA4.
 *
 * Rating is computed by web-vitals against Google's thresholds:
 *   LCP ≤ 2.5s   = good     ≤ 4.0s   = needs-improvement   else poor
 *   INP ≤ 200ms  = good     ≤ 500ms  = needs-improvement   else poor
 *   CLS ≤ 0.1    = good     ≤ 0.25   = needs-improvement   else poor
 */
function send(metric: Metric) {
  const isCls = metric.name === "CLS";
  sendGoogleEvent("web_vitals", {
    metric_name: metric.name,
    metric_id: metric.id,
    metric_value: Math.round(isCls ? metric.value * 1000 : metric.value),
    metric_rating: metric.rating, // "good" | "needs-improvement" | "poor"
    metric_delta: Math.round(isCls ? metric.delta * 1000 : metric.delta),
    navigation_type: metric.navigationType, // "navigate" | "reload" | "back-forward" | "prerender" | "back-forward-cache"
    event_label: metric.id,
    non_interaction: true,
  });
}

export function reportWebVitals() {
  // Use `reportAllChanges: false` (default) so each metric reports once when
  // it's settled — that's what Google ranks on. The metrics fire async and
  // the listeners survive page lifecycle events (bfcache, hidden tab, etc.).
  onLCP(send);
  onCLS(send);
  onINP(send);
  onFCP(send);
  onTTFB(send);
}
